import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  getGridSingleSelectOperators,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
  GridSortModel,
  GridFilterModel,
  GridRowIdGetter,
  GridFilterOperator,
  GridFilterItem,
  GridActionsCellItemProps,
  GridColumnVisibilityModel,
  GridLocaleText,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
  Edit,
  Preview,
  CloudDownload,
  CloudUpload,
  ContentPasteOff,
  AssignmentTurnedIn,
  Delete,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '@tabeeb/store'
import { TenantPermission } from '@tabeeb/enums'
import {
  ConfirmationDialog,
  DataGridV2,
  useDebouncedValue,
  useDialogState,
  usePagination,
} from '@collaborate-health/uikit'

import { hasTenantPermission } from '@tabeeb/modules/permissions/selectors'
import { getMe } from '@tabeeb/modules/account/selectors'

import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'

import {
  GetCertificateResponse,
  GetCertificatesFilterModelDto,
  GetCertificatesFilterOperator,
  GetCertificatesFilterColumn,
  GetCertificatesSortColumn,
  SortingOrder,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

import { CertificatesDisplayType } from '../../constants'
import {
  CertificateStatusType,
  CertificateStatusTypeDisplayName,
  CertificateScopeTypeDisplayName,
  CertificateScopeType,
  Scope,
} from '../../enums'
import { getReviewAllowed } from '../../helpers'
import { useCertificates, useCertificateCategories } from '../../hooks'
import { Certificate, CertificateType } from '../../types'

import { CertificateTypeGridFilterOperator, LocationGridFilterOperator, UserGridFilterOperator } from '../Filters'

import AddEditCertificateDialog, { AddEditCertificateDialogProps } from '../AddEditCertificateDialog'
import CertificatesGridToolbar, { CertificatesGridToolbarProps } from './CertificatesGridToolbar'
import CertificateOwner from '../CertificateOwner'
import CertificatesImageViewer from '../CertificatesImageViewer'
import CertificateStatusChip from '../CertificateStatusChip'
import CertificateScopeChip from '../CertificateScopeChip'
import CertificateThumbnail from '../CertificateThumbnail'
import RejectCertificateDialog from '../RejectCertificateDialog'
import ViewCertificateDialog from '../ViewCertificateDialog'

const dateFilterOperators = getGridDateOperators().filter(({ value }) => ['after', 'before'].includes(value))

const ownerFilterOperator = getGridStringOperators()
  .filter(({ value }) => value === 'contains')
  .map((operator) => ({ ...operator, InputComponent: UserGridFilterOperator }))

const filterValueFormatter = (filter: GridFilterItem) => {
  switch (filter.field) {
    case 'Owner':
      return JSON.stringify(filter.value.map((u: UserResponse) => ({ Id: u.Id, IdentityGuid: u.IdentityGuid })))
    case 'TypeName':
      return JSON.stringify(filter.value.map((ct: CertificateType) => ct.Id))
    case 'Location':
      return JSON.stringify(filter.value)
    default:
      return JSON.stringify(filter.value)
  }
}

export interface CertificatesGridProps {
  scope?: Scope
  contractorSourceTenantId?: number
  user?: { Id: number; IdentityGuid: string }
  contractVersion?: { Id: number }
  displayType: CertificatesDisplayType
  simpleView?: boolean
  toolbar?: React.FC<CertificatesGridToolbarProps> | null
}

const getRowId: GridRowIdGetter<GetCertificateResponse> = (row) => row.Id

const CertificatesGrid: React.FC<CertificatesGridProps> = ({
  scope: initialScope = Scope.Tenant,
  contractorSourceTenantId,
  user,
  contractVersion,
  displayType = CertificatesDisplayType.All,
  simpleView = false,
  toolbar: Toolbar = CertificatesGridToolbar,
}) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(getMe)
  const gridApiRef = useGridApiRef()

  const [scope, setScope] = useState<Scope>(initialScope)
  const [totalCount, setTotalCount] = useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>()
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>()
  const [search, setSearch] = useState<string>('')
  const debouncedSearch = useDebouncedValue(search, 500)

  const sorting = useMemo<{ field: GetCertificatesSortColumn; order: SortingOrder } | null>(() => {
    if (!sortModel || sortModel.length === 0) {
      return null
    }

    const sortItem = sortModel[0]
    const fieldReverseMap: Record<string, GetCertificatesSortColumn> = {
      CreatedOn: GetCertificatesSortColumn.CreatedOn,
      ValidFrom: GetCertificatesSortColumn.ValidFrom,
      ExpirationDate: GetCertificatesSortColumn.ExpirationDate,
    }

    if (!fieldReverseMap[sortItem.field] || !sortItem.sort) {
      return null
    }

    return {
      field: fieldReverseMap[sortItem.field],
      order: sortItem.sort === 'asc' ? SortingOrder.Asc : SortingOrder.Desc,
    }
  }, [sortModel])

  const filtering = useMemo<GetCertificatesFilterModelDto[]>(() => {
    const fieldReverseMap: Record<string, GetCertificatesFilterColumn> = {
      StatusId: GetCertificatesFilterColumn.StatusId,
      ValidFrom: GetCertificatesFilterColumn.ValidFrom,
      ExpirationDate: GetCertificatesFilterColumn.ExpirationDate,
      Owner: GetCertificatesFilterColumn.Owner,
      Location: GetCertificatesFilterColumn.Location,
      ScopeId: GetCertificatesFilterColumn.Scope,
      CategoryId: GetCertificatesFilterColumn.Category,
      TypeName: GetCertificatesFilterColumn.Type,
    }

    const operatorReverseMap: Record<string, GetCertificatesFilterOperator> = {
      isAnyOf: GetCertificatesFilterOperator.IsAnyOf,
      is: GetCertificatesFilterOperator.Is,
      after: GetCertificatesFilterOperator.After,
      before: GetCertificatesFilterOperator.Before,
      between: GetCertificatesFilterOperator.Between,
      typeIs: GetCertificatesFilterOperator.TypeIs,
      contains: GetCertificatesFilterOperator.Contains,
    }

    const apiFilters: GetCertificatesFilterModelDto[] = (filterModel?.items || [])
      .filter((item) => item.value && (Array.isArray(item.value) ? item.value.length > 0 : true))
      .map((item) => ({
        Field: fieldReverseMap[item.field],
        Operator: operatorReverseMap[item.operator],
        Value: filterValueFormatter(item),
      }))
      .filter((f: GetCertificatesFilterModelDto) => f.Field !== undefined && f.Operator !== undefined)

    return apiFilters
  }, [filterModel])

  const pagination = usePagination({ total: totalCount, pageSize: 25 })

  const {
    loading,
    certificates,
    totalCount: certificatesTotalCount,
    updateAsync,
    createAsync,
    reviewAsync,
    deleteAsync,
    onReload,
  } = useCertificates({
    search: debouncedSearch,
    scope,
    sorting,
    filtering,
    pageNumber: pagination.current - 1,
    pageSize: pagination.size,
    contractorSourceTenantId,
    userGuid: user?.IdentityGuid,
    userId: user?.Id,
    contractVersionId: contractVersion?.Id,
    displayType,
  })

  const { categories } = useCertificateCategories()

  useEffect(() => {
    if (!loading) {
      setTotalCount(certificatesTotalCount)
    }
  }, [certificatesTotalCount, loading])

  const isPersonal = useMemo(() => {
    return displayType === CertificatesDisplayType.Personal
  }, [displayType])

  const certificateTypeCustomFilterOperator = useMemo<GridFilterOperator<GetCertificateResponse>>(
    () => ({
      label: 'is any of',
      value: 'isAnyOf',
      getApplyFilterFn: () => {
        return () => true
      },
      InputComponent: CertificateTypeGridFilterOperator,
      InputComponentProps: {
        personal: isPersonal,
      },
    }),
    [isPersonal]
  )

  const locationFilterOperator = useMemo<GridFilterOperator<GetCertificateResponse>>(
    () => ({
      label: 'is any of',
      value: 'isAnyOf',
      getApplyFilterFn: () => {
        return () => true
      },
      InputComponent: LocationGridFilterOperator,
    }),
    []
  )

  const notifySuccessAndReload = useCallback(
    (message: string) => {
      dispatch(onAddSuccessNotification({ message }))
      onReload()
    },
    [onReload, dispatch]
  )

  const [viewDialogOpen, onViewDialogOpen, onViewDialogClose] = useDialogState()

  const [addEditDialogOpen, onAddEditDialogOpen, onAddEditDialogClose] = useDialogState()
  const [selectedCertificateId, setSelectedCertificateId] = useState<number | null>(null)

  const selectedCertificate = useMemo(() => {
    return certificates.find((c) => c.Id === selectedCertificateId) || null
  }, [certificates, selectedCertificateId])

  const onAddEditDialogSubmit = useCallback<AddEditCertificateDialogProps['onSubmit']>(
    async ({ values, editMode }) => {
      if (editMode) {
        await updateAsync(
          {
            Id: selectedCertificateId!,
            StatusId:
              selectedCertificate?.StatusId === CertificateStatusType.Rejected
                ? CertificateStatusType.PendingReview
                : selectedCertificate?.StatusId ?? CertificateStatusType.Draft,
            ...values,
          },
          {
            onSuccess: () => notifySuccessAndReload('Certificate was successfully updated'),
            onError: () => dispatch(onAddErrorNotification({ message: 'Failed to update certificate' })),
          }
        )
      } else {
        await createAsync(values, {
          onSuccess: () => notifySuccessAndReload('Certificate was successfully created'),
          onError: () => dispatch(onAddErrorNotification({ message: 'Failed to create certificate' })),
        })
      }

      onAddEditDialogClose()
    },
    [
      onAddEditDialogClose,
      updateAsync,
      selectedCertificateId,
      selectedCertificate?.StatusId,
      notifySuccessAndReload,
      dispatch,
      createAsync,
    ]
  )

  const hasManageCertificatesTenantScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.CertificatesTenantScope)
  )
  const hasManageCertificatesGlobalScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.CertificatesGlobalScope)
  )

  const handleEditClick = useCallback(
    (certificate: Certificate) => {
      setSelectedCertificateId(certificate.Id)
      onAddEditDialogOpen()
    },
    [onAddEditDialogOpen]
  )

  const handleViewClick = useCallback(
    (certificate: Certificate) => {
      setSelectedCertificateId(certificate.Id)
      onViewDialogOpen()
    },
    [onViewDialogOpen]
  )

  const handleSendToReviewClick = useCallback(
    async (certificate: Certificate) => {
      await updateAsync(
        {
          ...certificate,
          StatusId: CertificateStatusType.PendingReview,
          LocationIds: certificate.Locations.map((l) => l.Id),
        },
        {
          onSuccess: () => notifySuccessAndReload('Certificate was successfully sent to review'),
          onError: () => dispatch(onAddErrorNotification({ message: 'Failed to send certificate to review' })),
        }
      )
    },
    [updateAsync, notifySuccessAndReload, dispatch]
  )

  const handlePickUpFromReviewClick = useCallback(
    async (certificate: GetCertificateResponse) => {
      await updateAsync(
        {
          ...certificate,
          StatusId: CertificateStatusType.Draft,
          LocationIds: certificate.Locations.map((l) => l.Id),
        },
        {
          onSuccess: () => notifySuccessAndReload('Certificate was successfully picked up from review'),
          onError: () => dispatch(onAddErrorNotification({ message: 'Failed to pick up the certificate from review' })),
        }
      )
    },
    [updateAsync, notifySuccessAndReload, dispatch]
  )

  const [openDeleteConfirmation, onOpenDeleteConfirmation, onCloseDeleteConfirmation] = useDialogState()

  const handleDeleteClick = useCallback(
    (id: number) => {
      onOpenDeleteConfirmation()
      setSelectedCertificateId(id)
    },
    [onOpenDeleteConfirmation]
  )

  const handleConfirmDelete = useCallback(
    async (certificate: Certificate) => {
      await deleteAsync(
        {
          Id: certificate.Id,
          Subdomain: certificate.TenantSubdomain,
        },
        {
          onSuccess: () => notifySuccessAndReload('Certificate was successfully deleted'),
          onError: () => dispatch(onAddErrorNotification({ message: 'Failed to delete certificate' })),
        }
      )
    },
    [deleteAsync, notifySuccessAndReload, dispatch]
  )

  const [approveDialogOpen, onOpenApprovalConfirmationDialog, onCloseApprovalConfirmationDialog] = useDialogState()
  const [rejectDialogOpen, onOpenRejectionConfirmationDialog, onCloseRejectionConfirmationDialog] = useDialogState()

  const onReviewCertificateSubmit = useCallback(
    async ({
      id,
      statusId,
      rejectionReason,
      subdomain,
    }: {
      id: number
      statusId: CertificateStatusType.Approved | CertificateStatusType.Rejected
      rejectionReason?: string | null
      subdomain?: string | null
    }) => {
      try {
        await reviewAsync({ Id: id, StatusId: statusId, RejectionReason: rejectionReason, Subdomain: subdomain })

        notifySuccessAndReload('Certificate was successfully reviewed')
      } catch {
        dispatch(onAddErrorNotification({ message: 'Failed to review certificate' }))
      }
    },
    [reviewAsync, notifySuccessAndReload, dispatch]
  )

  const handleConfirmApprove = useCallback(() => {
    onCloseApprovalConfirmationDialog()
    onReviewCertificateSubmit({ id: selectedCertificateId!, statusId: CertificateStatusType.Approved })
  }, [onCloseApprovalConfirmationDialog, selectedCertificateId, onReviewCertificateSubmit])

  const handleConfirmReject = useCallback(
    (reason?: string | null) => {
      onCloseRejectionConfirmationDialog()
      onReviewCertificateSubmit({
        id: selectedCertificate?.Id!,
        subdomain: selectedCertificate?.TenantSubdomain,
        statusId: CertificateStatusType.Rejected,
        rejectionReason: reason,
      })
    },
    [selectedCertificate, onCloseRejectionConfirmationDialog, onReviewCertificateSubmit]
  )

  const [openImageViewer, onOpenImageViewer, onCloseImageViewer] = useDialogState()
  const [certificateCoverSrc, setCertificateCoverSrc] = useState('')

  const columns = useMemo<GridColDef<GetCertificateResponse>[]>(() => {
    const cols: GridColDef<GetCertificateResponse>[] = [
      {
        field: 'ThumbnailUrl',
        headerName: 'Image',
        align: 'center',
        renderCell: ({ row }) => (
          <CertificateThumbnail
            src={row.ThumbnailUrl ?? row.PictureUrl}
            onClick={() => {
              setCertificateCoverSrc(row.PictureUrl)
              onOpenImageViewer()
            }}
          />
        ),
        filterable: false,
        sortable: false,
        width: 90,
      },
      {
        field: 'SerialNumber',
        headerName: 'Serial number',
        sortable: false,
        filterable: false,
        flex: 1,
        valueGetter: ({ row }) => row.SerialNumber ?? '-',
      },
      {
        field: 'Authority',
        headerName: 'Authority',
        sortable: false,
        filterable: false,
        flex: 1,
        valueGetter: ({ row }) => row.Authority ?? '-',
      },
      {
        field: 'TypeName',
        headerName: 'Type',
        sortable: false,
        flex: 1,
        filterOperators: [certificateTypeCustomFilterOperator],
      },
      { field: 'Location', sortable: false, filterOperators: [locationFilterOperator] },
      {
        field: 'CategoryId',
        headerName: 'Category',
        sortable: false,
        type: 'singleSelect',
        valueOptions: categories.map((category) => ({
          value: category.Id,
          label: category.Name,
        })),
        filterOperators: getGridSingleSelectOperators().filter(({ value }) => value === 'is'),
        width: 110,
      },
      {
        field: 'ScopeId',
        headerName: 'Scope',
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          CertificateScopeType.ProfileCertificate,
          CertificateScopeType.UserCertificate,
          CertificateScopeType.TenantCertificate,
        ]
          .filter((value) => {
            if (isPersonal) {
              return value !== CertificateScopeType.TenantCertificate
            }

            return Boolean(hasManageCertificatesTenantScopePermission || hasManageCertificatesGlobalScopePermission)
          })
          .map((value) => ({
            value,
            label: CertificateScopeTypeDisplayName[value],
          })),
        filterOperators: getGridSingleSelectOperators().filter(({ value }) => value === 'is'),
        width: 110,
        renderCell: ({ row }) => <CertificateScopeChip scope={row.ScopeId} />,
      },
      {
        field: 'StatusId',
        headerName: 'Status',
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          CertificateStatusType.PendingReview,
          CertificateStatusType.Approved,
          CertificateStatusType.Rejected,
          CertificateStatusType.Expired,
          CertificateStatusType.Draft,
          CertificateStatusType.Obsolete,
          CertificateStatusType.Processed,
        ].map((value) => ({
          value,
          label: CertificateStatusTypeDisplayName[value],
        })),
        filterOperators: getGridSingleSelectOperators().filter(({ value }) => value === 'isAnyOf'),
        width: 165,
        renderCell: ({ row }) => <CertificateStatusChip status={row.StatusId} />,
      },
      {
        field: 'Owner',
        headerName: 'Owner',
        sortable: false,
        flex: 1,
        filterOperators: ownerFilterOperator,
        ...((isPersonal || Boolean(user?.IdentityGuid)) && { filterable: false }),
        renderCell: ({ row }) => {
          return <CertificateOwner certificate={row} />
        },
      },
      {
        field: 'ValidFrom',
        headerName: 'Valid from',
        type: 'dateTime',
        filterOperators: dateFilterOperators,
        valueGetter: ({ value }) => value && new Date(value),
        valueFormatter: ({ value }) => value && value.toLocaleDateString('en-US'),
        width: 90,
      },
      {
        field: 'ExpirationDate',
        headerName: 'Valid till',
        type: 'dateTime',
        filterOperators: dateFilterOperators,
        valueGetter: ({ value }) => value && new Date(value),
        valueFormatter: ({ value }) => value && value.toLocaleDateString('en-US'),
        width: 90,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: ({ row }) => {
          const actions: React.ReactElement<GridActionsCellItemProps>[] = []

          const isEditLockedByStatus = ![
            CertificateStatusType.Draft,
            CertificateStatusType.Obsolete,
            CertificateStatusType.Rejected,
          ].includes(row.StatusId)

          if (!simpleView && !isEditLockedByStatus) {
            const canEditOrDelete =
              isPersonal ||
              ((row.OwnerProfileId ? row.OwnerProfileId === currentUser.Id : true) &&
                (row.OwnerIdentityGuid ? row.OwnerIdentityGuid === currentUser.IdentityGuid : true) &&
                (row.ScopeId === CertificateScopeType.TenantCertificate && row.TenantId
                  ? row.TenantId === currentUser.TenantId
                  : true))

            if (canEditOrDelete) {
              actions.push(
                <GridActionsCellItem
                  icon={<Edit />}
                  label='Edit'
                  title='Edit'
                  onClick={() => handleEditClick(row)}
                  showInMenu
                />,
                <GridActionsCellItem
                  icon={<Delete />}
                  label='Delete'
                  title='Delete'
                  onClick={() => handleDeleteClick(row.Id)}
                  showInMenu
                />
              )
            }
          }

          if (!simpleView) {
            const canReview =
              ((row.ScopeId === CertificateScopeType.TenantCertificate && row.TenantId === currentUser.TenantId) ||
                hasManageCertificatesGlobalScopePermission ||
                hasManageCertificatesTenantScopePermission ||
                isPersonal) &&
              [CertificateStatusType.Draft, CertificateStatusType.PendingReview].includes(row.StatusId)

            if (canReview) {
              if (row.StatusId === CertificateStatusType.Draft) {
                actions.push(
                  <GridActionsCellItem
                    icon={<CloudUpload />}
                    label='Send for review'
                    title='Send for review'
                    onClick={() => handleSendToReviewClick(row)}
                    showInMenu
                  />
                )
              } else {
                actions.push(
                  <GridActionsCellItem
                    icon={<CloudDownload />}
                    title='Pick up from review'
                    label='Pick up from review'
                    onClick={() => handlePickUpFromReviewClick(row)}
                    showInMenu
                  />
                )
              }
            }
          }

          actions.push(
            <GridActionsCellItem
              icon={<Preview />}
              label='View'
              title='View'
              onClick={() => handleViewClick(row)}
              showInMenu
            />
          )

          if (!simpleView) {
            const isReviewAllowed = getReviewAllowed({
              personal: isPersonal,
              certificate: row,
              hasManageCertificatesTenantScopePermission,
              hasManageCertificatesGlobalScopePermission,
            })

            if (isReviewAllowed) {
              actions.push(
                <GridActionsCellItem
                  icon={<ContentPasteOff />}
                  label='Reject'
                  title='Reject'
                  onClick={() => {
                    setSelectedCertificateId(row.Id)
                    onOpenRejectionConfirmationDialog()
                  }}
                  showInMenu
                />,
                <GridActionsCellItem
                  icon={<AssignmentTurnedIn />}
                  label='Approve'
                  title='Approve'
                  onClick={() => {
                    setSelectedCertificateId(row.Id)
                    onOpenApprovalConfirmationDialog()
                  }}
                  showInMenu
                />
              )
            }
          }

          return actions
        },
      },
    ]

    return cols
  }, [
    certificateTypeCustomFilterOperator,
    locationFilterOperator,
    categories,
    isPersonal,
    user?.IdentityGuid,
    onOpenImageViewer,
    hasManageCertificatesTenantScopePermission,
    hasManageCertificatesGlobalScopePermission,
    simpleView,
    currentUser.Id,
    currentUser.IdentityGuid,
    currentUser.TenantId,
    handleEditClick,
    handleDeleteClick,
    handleSendToReviewClick,
    handlePickUpFromReviewClick,
    handleViewClick,
    onOpenRejectionConfirmationDialog,
    onOpenApprovalConfirmationDialog,
  ])

  const visibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    return {
      Owner: !isPersonal,
      Location: false,
      ScopeId: false,
      CategoryId: false,
    }
  }, [isPersonal])

  const localeText = useMemo<Partial<GridLocaleText>>(
    () => ({
      noRowsLabel: 'No certificates found',
      noResultsOverlayLabel: 'No certificates found',
    }),
    []
  )

  return (
    <>
      {Toolbar && (
        <Toolbar
          filtersActive={Boolean(filterModel && filterModel.items.length > 0)}
          showScopeSelector={!isPersonal}
          showAddButton={!simpleView}
          onOpenAddEditDialog={onAddEditDialogOpen}
          onReload={onReload}
          loading={loading}
          onSearchChange={setSearch}
          gridApiRef={gridApiRef}
          searchText={search}
          scope={scope}
          onScopeChange={setScope}
        />
      )}
      <DataGridV2
        apiRef={gridApiRef}
        loading={loading}
        rows={certificates}
        getRowId={getRowId}
        columns={columns}
        rowCount={totalCount}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        paginationState={pagination}
        rowHeight={64}
        columnVisibilityModel={visibilityModel}
        onRowDoubleClick={({ row }) => handleViewClick(row)}
        localeText={localeText}
        disableMultipleColumnsFiltering={false}
      />
      <AddEditCertificateDialog
        open={addEditDialogOpen}
        certificate={selectedCertificate}
        onClose={onAddEditDialogClose}
        onSubmit={onAddEditDialogSubmit}
        onExited={() => {
          setSelectedCertificateId(null)
        }}
      />
      <CertificatesImageViewer open={openImageViewer} src={certificateCoverSrc} onClose={onCloseImageViewer} />
      <ViewCertificateDialog
        open={viewDialogOpen}
        certificate={selectedCertificate}
        onReview={onReviewCertificateSubmit}
        personal={isPersonal}
        onClose={onViewDialogClose}
        onExited={() => {
          setSelectedCertificateId(null)
        }}
      />
      <ConfirmationDialog
        isOpen={openDeleteConfirmation}
        title='Are you sure you want to delete this certificate?'
        cancelButtonText='No'
        submitButtonText='Yes'
        onCloseDialog={() => {
          onCloseDeleteConfirmation()
          setSelectedCertificateId(null)
        }}
        onSubmit={() => {
          onCloseDeleteConfirmation()
          if (selectedCertificate !== null) {
            handleConfirmDelete(selectedCertificate)
          }
          setSelectedCertificateId(null)
        }}
      />
      <ConfirmationDialog
        isOpen={approveDialogOpen}
        title='Are you sure you want to approve the certificate?'
        submitButtonText='Approve'
        cancelButtonText='Cancel'
        onCloseDialog={onCloseApprovalConfirmationDialog}
        onSubmit={handleConfirmApprove}
      />
      <RejectCertificateDialog
        open={rejectDialogOpen}
        onClose={onCloseRejectionConfirmationDialog}
        onSubmit={handleConfirmReject}
      />
    </>
  )
}

export default CertificatesGrid

